import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import apiClient from '../../api/client'; // If not already imported
import { Box, Typography, Paper, Divider, TextField, Button, FormControl, InputLabel, 
  Select, MenuItem, Switch, FormControlLabel, Alert, CircularProgress, Accordion, 
  AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useEmailSenders, useVerifiedDomains } from '../../hooks/useSettings';
import { useCurrency } from '../../hooks/useCurrency';
import allTimezones from '../../constants/timezones';
import ThemeCustomization from '../../components/settings/ThemeCustomization';
import ApiKeyManagement from '../../components/settings/ApiKeyManagement';
import XeroApiKeyManagement from '../../components/settings/XeroApiKeyManagement';
import WebhookManagement from '../../components/settings/WebhookManagement';
import SubscriptionManagement from '../../components/SubscriptionManagement';
import MfaSettings from '../../components/settings/MfaSettings';
import ChangePasswordSettings from '../../components/settings/ChangePasswordSettings';
import { getAvailableCurrencies, getCurrency, setCurrencyPreference } from '../../utils/currency';
import { useAppTheme } from '../../contexts/ThemeContext';

const SnackbarAlert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Settings = () => {
  console.log('Rendering Settings page');
  const { currentTheme } = useAppTheme();
  
  // State for email sender form
  const [senderName, setSenderName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  
  // State for domain verification form
  const [domain, setDomain] = useState('');
  const [verificationStep, setVerificationStep] = useState('initial'); // initial, pending, verified
  const [dnsRecords, setDnsRecords] = useState([]);
  const [currentDomainId, setCurrentDomainId] = useState(null);
  
  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Timezone state
  const [timezone, setTimezone] = useState('UTC');
  const [isTimezoneLoading, setIsTimezoneLoading] = useState(false);
  
  // Currency hook
  const { currency, isLoading: isCurrencyLoading, error: currencyError, updateCurrency } = useCurrency();
  const availableCurrencies = getAvailableCurrencies();
  const [selectedCurrency, setSelectedCurrency] = useState(currency);
  
  // Update selected currency when hook currency changes
  useEffect(() => {
    setSelectedCurrency(currency);
  }, [currency]);
  
  // Timezone initialization
  useEffect(() => {
    // Function to load timezone from API or local storage
    const loadTimezone = async () => {
      try {
        setIsTimezoneLoading(true);
        
        // First try to load from API
        const token = localStorage.getItem('auth_token');
        if (token) {
          try {
            const response = await apiClient.get('/settings/timezone', {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            
            if (response.data && response.data.timezone) {
              console.log('Loaded timezone from API:', response.data.timezone);
              setTimezone(response.data.timezone);
              localStorage.setItem('timezone', response.data.timezone); // Update local storage
              setIsTimezoneLoading(false);
              return;
            }
          } catch (apiError) {
            console.log('Could not load timezone from API, falling back to localStorage:', apiError);
            // Continue to fallback methods
          }
        }
        
        // Fallback to localStorage if API fails
        const savedTimezone = localStorage.getItem('timezone');
        
        if (savedTimezone) {
          setTimezone(savedTimezone);
        } else {
          // Final fallback: use browser timezone
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
          setTimezone(browserTimezone);
          localStorage.setItem('timezone', browserTimezone); // Save it for next time
        }
      } catch (error) {
        console.error('Error loading timezone:', error);
        // Ultimate fallback - set UTC
        setTimezone('UTC');
      } finally {
        setIsTimezoneLoading(false);
      }
    };
    
    loadTimezone();
  }, []);

  // Currency initialization
  useEffect(() => {
    const loadCurrency = () => {
      try {
        // Get current currency preference
        const currentCurrency = getCurrency();
        setSelectedCurrency(currentCurrency);
        console.log('Loaded currency preference:', currentCurrency);
      } catch (error) {
        console.error('Error loading currency:', error);
        setSelectedCurrency('USD'); // Fallback to USD
      }
    };
    
    loadCurrency();
  }, []);

  // Handler to save timezone
  const handleSaveTimezone = async () => {
    try {
      setIsTimezoneLoading(true);
      const token = localStorage.getItem('auth_token');
      await apiClient.patch('/settings/timezone', { timezone }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Update localStorage
      localStorage.setItem('timezone', timezone);
      
      // Notify user
      setNotification({
        open: true,
        message: 'Timezone updated successfully',
        severity: 'success',
      });
      
      // Broadcast timezone change event for other components
      const timezoneEvent = new CustomEvent('timezoneChanged', { 
        detail: { timezone } 
      });
      window.dispatchEvent(timezoneEvent);
      
    } catch (error) {
      console.error('Error updating timezone:', error);
      setNotification({
        open: true,
        message: 'Failed to update timezone',
        severity: 'error',
      });
    } finally {
      setIsTimezoneLoading(false);
    }
  };
  
  // Handler to save currency
  const handleSaveCurrency = async () => {
    try {
      const result = await updateCurrency(selectedCurrency);
      
      if (result.success) {
        setNotification({
          open: true,
          message: result.message,
          severity: 'success',
        });
      } else {
        setNotification({
          open: true,
          message: result.message,
          severity: 'error',
        });
      }
      
    } catch (error) {
      console.error('Error updating currency:', error);
      setNotification({
        open: true,
        message: 'Failed to update currency preference',
        severity: 'error',
      });
    }
  };
  
  // Get data from custom hooks
  const { emailSenders, isLoading: isLoadingSenders, addSender } = useEmailSenders();
  const { 
    domains, 
    isLoading: isLoadingDomains, 
    addDomain, 
    checkVerification,
    createSenderWithDomain,
    refreshDomains
  } = useVerifiedDomains();
  
  // Handler for adding a new sender
  const handleAddSender = async () => {
    if (!senderName || !emailAddress) return;
    
    try {
      let result;
      
      if (selectedDomain) {
        // Creating a sender with a verified domain
        result = await createSenderWithDomain(
          { display_name: senderName, email: emailAddress },
          selectedDomain
        );
        
        setNotification({
          open: true,
          message: 'Email sender added with verified domain',
          severity: 'success'
        });
      } else {
        // Regular sender without domain verification
        result = await addSender({
          display_name: senderName,
          email: emailAddress,
          smtp_host: "smtp.sendgrid.net",
          smtp_port: 587,
          smtp_username: "apikey",
          smtp_password: ""
        });
        
        setNotification({
          open: true,
          message: 'Email sender added successfully',
          severity: 'success'
        });
      }
      
      // Reset form
      setSenderName('');
      setEmailAddress('');
      setSelectedDomain('');
    } catch (error) {
      console.error('Error adding sender:', error);
      setNotification({
        open: true,
        message: error.response?.data?.detail || 'Failed to add email sender',
        severity: 'error'
      });
    }
  };
  
  // Handler for initiating domain verification
  const handleAddDomain = async () => {
    if (!domain) return;
    
    try {
      console.log("Starting domain verification for:", domain);
      const result = await addDomain(domain);
      console.log("Domain verification result:", result);
      
      if (result.status === 'pending_verification') {
        setVerificationStep('pending');
        setDnsRecords(result.dns_records);
        setCurrentDomainId(result.domain_id);
        
        setNotification({
          open: true,
          message: 'Domain added. Please add DNS records to verify ownership.',
          severity: 'info'
        });
      } else if (result.status === 'already_verified') {
        setNotification({
          open: true,
          message: 'This domain is already verified.',
          severity: 'info'
        });
        
        // Refresh the domains list
        await refreshDomains();
      } else if (result.status === 'error') {
        setNotification({
          open: true,
          message: `Error: ${result.message}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error("Error in domain verification:", error);
      setNotification({
        open: true,
        message: 'Failed to start domain verification',
        severity: 'error'
      });
    }
  };
  
  // Handler for checking domain verification status
  const handleCheckVerification = async (domainId) => {
    if (!domainId) {
      console.error("No domain ID provided for verification check");
      return;
    }
    
    try {
      console.log(`Checking verification for domain ID: ${domainId}`);
      const result = await checkVerification(domainId);
      console.log("Verification check result:", result);
      
      if (result.status === 'verified') {
        setVerificationStep('verified');
        setNotification({
          open: true,
          message: 'Domain verified successfully!',
          severity: 'success'
        });
        
        // Refresh domains list to show updated status
        await refreshDomains();
      } else if (result.status === 'verification_failed') {
        setNotification({
          open: true,
          message: `Verification failed: ${result.message}`,
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error("Error checking verification:", error);
      setNotification({
        open: true,
        message: 'Failed to check domain verification',
        severity: 'error'
      });
    }
  };
  
  // Copy DNS record to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setNotification({
      open: true,
      message: 'Copied to clipboard!',
      severity: 'success'
    });
  };
  
  // Close notification
  const handleCloseNotification = () => {
    setNotification({...notification, open: false});
  };

  // Wrapper for subscription notifications to ensure open: true
  const handleSubscriptionNotification = (notificationData) => {
    setNotification({
      open: true,
      ...notificationData
    });
  };
  
  return (
    <Box>
      {/* Settings Header */}
      {currentTheme.isGradient ? (
        <Box className="page-header-gradient" sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Settings
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Configure your clinic's system preferences
          </Typography>
        </Box>
      ) : (
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
      )}

      {/* Timezone Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          System Timezone
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            The system timezone affects when your automations run and how dates/times are displayed throughout the application.
          </Typography>
        </Alert>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="timezone-label">Timezone</InputLabel>
          <Select
            labelId="timezone-label"
            value={timezone}
            label="Timezone"
            onChange={(e) => setTimezone(e.target.value)}
            disabled={isTimezoneLoading}
          >
            {allTimezones.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleSaveTimezone}
          disabled={isTimezoneLoading}
          startIcon={isTimezoneLoading ? <CircularProgress size={20} /> : null}
        >
          {isTimezoneLoading ? 'Saving...' : 'Save Timezone'}
        </Button>
      </Box>

      {/* Currency Settings Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Currency Settings
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Set your preferred currency for displaying payment amounts, invoices, and financial data throughout the application.
          </Typography>
        </Alert>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="currency-label">Currency</InputLabel>
          <Select
            labelId="currency-label"
            value={selectedCurrency}
            label="Currency"
            onChange={(e) => setSelectedCurrency(e.target.value)}
            disabled={isCurrencyLoading}
          >
            {availableCurrencies.map((curr) => (
              <MenuItem key={curr.code} value={curr.code}>
                {curr.symbol} {curr.name} ({curr.code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleSaveCurrency}
          disabled={isCurrencyLoading}
          startIcon={isCurrencyLoading ? <CircularProgress size={20} /> : null}
        >
          {isCurrencyLoading ? 'Saving...' : 'Save Currency'}
        </Button>
      </Box>

      {/* Theme Customization Section */}
      <ThemeCustomization />

      {/* Change Password Section */}
      <ChangePasswordSettings onNotification={(data) => setNotification({ ...data, open: true })} />

      {/* MFA Settings Section */}
      <MfaSettings onNotification={(data) => setNotification({ ...data, open: true })} />

      {/* API Key Management Section */}
      <ApiKeyManagement />

      {/* Xero API Key Management Section */}
      <XeroApiKeyManagement />

      {/* Webhook Management Section */}
      <WebhookManagement />

      {/* Email Senders Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Settings
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Email Senders
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Sender Name"
              fullWidth
              sx={{ mb: 2 }}
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
            />
            <TextField
              label="Email Address"
              fullWidth
              sx={{ mb: 2 }}
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
            />
            {domains && domains.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Verified Domain (Optional)</InputLabel>
                <Select
                  value={selectedDomain}
                  label="Verified Domain (Optional)"
                  onChange={(e) => setSelectedDomain(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {domains.filter(d => d.is_verified).map(domain => (
                    <MenuItem key={domain.id} value={domain.id}>
                      {domain.domain}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button 
              variant="contained" 
              onClick={handleAddSender}
              disabled={isLoadingSenders || !senderName || !emailAddress}
            >
              {isLoadingSenders ? <CircularProgress size={24} /> : 'Add Sender'}
            </Button>
          </Box>
          
          <Typography variant="subtitle2" color="text.secondary">
            Added Senders:
          </Typography>
          <Box sx={{ mb: 2 }}>
            {emailSenders && emailSenders.map(sender => (
              <Paper key={sender.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Typography>
                  {sender.display_name} ({sender.email}) 
                  {sender.domain_id && <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 1 }} />}
                </Typography>
              </Paper>
            ))}
            {(!emailSenders || emailSenders.length === 0) && (
              <Typography color="text.secondary">No senders added yet</Typography>
            )}
          </Box>
        </Box>
        
        {/* Domain Verification Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Verified Domains
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Verifying your domain improves email deliverability. Add your domain below and follow the instructions to verify ownership.
          </Alert>
          
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Domain Name (e.g., yourclinic.com)"
              fullWidth
              sx={{ mb: 2 }}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
            <Button 
              variant="contained" 
              onClick={handleAddDomain}
              disabled={isLoadingDomains || !domain || verificationStep === 'pending'}
            >
              {isLoadingDomains ? <CircularProgress size={24} /> : 'Verify Domain'}
            </Button>
          </Box>
          
          {/* Domain Verification Steps */}
          {verificationStep === 'pending' && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>DNS Records to Add</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Add these DNS records to your domain's DNS settings to verify ownership:
                </Typography>
                
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Host</TableCell>
                        <TableCell>Value</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dnsRecords.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>{record.type}</TableCell>
                          <TableCell>{record.host}</TableCell>
                          <TableCell>{record.value}</TableCell>
                          <TableCell>
                            <Button 
                              size="small" 
                              startIcon={<ContentCopyIcon />}
                              onClick={() => copyToClipboard(record.value)}
                            >
                              Copy
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Typography paragraph>
                  After adding the DNS records, DNS changes may take up to 48 hours to propagate. 
                  Click the button below to check verification status:
                </Typography>
                <Button 
                  variant="contained" 
                  color="secondary"
                  onClick={() => handleCheckVerification(currentDomainId)}
                  disabled={!currentDomainId}
                >
                  Check Verification Status
                </Button>
              </AccordionDetails>
            </Accordion>
          )}
          
          {/* List of domains */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3 }}>
            Your Domains:
          </Typography>
          <Box>
            {domains && domains.map(domain => (
              <Paper key={domain.id} variant="outlined" sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography>{domain.domain}</Typography>
                </Box>
                {domain.is_verified ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ErrorIcon color="warning" sx={{ mr: 1 }} />
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleCheckVerification(domain.id)}
                    >
                      Check Status
                    </Button>
                  </Box>
                )}
              </Paper>
            ))}
            {(!domains || domains.length === 0) && (
              <Typography color="text.secondary">No domains added yet</Typography>
            )}
          </Box>
        </Box>
      </Paper>
      
      {/* Rest of your settings page stays the same */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Automation Settings
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <FormControlLabel 
          control={<Switch defaultChecked />} 
          label="Enable Email Tracking" 
          sx={{ mb: 2, display: 'block' }}
        />
        
        <FormControlLabel 
          control={<Switch defaultChecked />} 
          label="Send Notification on Campaign Completion" 
          sx={{ mb: 2, display: 'block' }}
        />
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Default Schedule Type</InputLabel>
          <Select
            value="DAILY"
            label="Default Schedule Type"
          >
            <MenuItem value="DAILY">Daily</MenuItem>
            <MenuItem value="WEEKLY">Weekly</MenuItem>
            <MenuItem value="MONTHLY">Monthly</MenuItem>
          </Select>
        </FormControl>
      </Paper>
      
      {/* Subscription Management Section - moved to bottom */}
      <SubscriptionManagement onNotification={handleSubscriptionNotification} />
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained">
          Save Settings
        </Button>
      </Box>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarAlert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </SnackbarAlert>
      </Snackbar>
      </Box>
  );
};

export default Settings;